import { Routes, Route, Outlet } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
// import "ag-grid-community/styles/ag-grid.css"; // Core grid CSS, always needed
// import "ag-grid-community/styles/ag-theme-alpine.css";
import "react-datepicker/dist/react-datepicker.css";
import ErrorBoundary from "../layouts/ErrorBoundary";
import AuthProvider from "../context/AuthProvider";
import AppProvider from "../context/AppProvider";
import AuthLayout from "../layouts/AuthLayout";
import MainLayout from "../layouts/MainLayout";
import Missing from "../layouts/Missing";
import Login from "./auth/Login";
import CheckLogin from "./auth/CheckLogin";
import ForgotPassword from "./auth/ForgotPassword";
import ClientAccountView from "./auth/ClientAccountView";
import UserForm from "./users/UserForm";
import UserList from "./users/UserList";
import UserView from "./users/UserView";
import Dashboard from "./users/Dashboard";
import Desktop from "./users/Desktop";
import Profile from "./users/Profile";
import Approval from "./users/Approval";

import ClientList from "./clients/ClientList";
import ClientForm from "./clients/ClientForm";
import ClientView from "./clients/ClientView";

import CompanyList from "./companies/CompanyList";
import CompanyForm from "./companies/CompanyForm";
import CompanyView from "./companies/CompanyView";

import CompanyTypeList from "./companies/CompanyTypeList";
import CompanyTypeView from "./companies/CompanyTypeView";

import BranchList from "./branches/BranchList";
import BranchForm from "./branches/BranchForm";
import BranchView from "./branches/BranchView";

import PolicyList from "./policies/PolicyList";
import PolicyForm from "./policies/PolicyForm";
import PolicyView from "./policies/PolicyView";

import PolicyTypeList from "./policy-types/PolicyTypeList";
import PolicyTypeForm from "./policy-types/PolicyTypeForm";
import PolicyTypeView from "./policy-types/PoliccyTypeView";
import PolicyTypeProductList from "./policy-types/PolicyTypeProductList";

import ProductList from "./products/ProductList";
import ProductForm from "./products/ProductForm";
import ProductView from "./products/ProductView";

import ClaimList from "./claims/ClaimList";
import ClaimForm from "./claims/ClaimForm";
import ClaimView from "./claims/ClaimView";

import RequirementList from "./policy-requirements/RequirementList";
import RequirementForm from "./policy-requirements/RequirementForm";

import CommissionList from "./commissions/CommissionList";
import DeferredCommissionList from "./commissions/DeferredCommissionList";

import RevokeRequestList from "./policies/RevokeRequestList";

import {
  SmsMessaging,
  EmailMessaging,
  NotificationSetting,
  Notification,
} from "./";
import ClientAccount from "./clients/ClientAccount";
import ClientExpiringPolices from "./clients/ClientExpiringPolices";
import ClientPolicies from "./policies/ClientPolicies";
import ContactUsWidget from "../widgets/ContactUsWidget";

// client user components
import ClientAccountClaims from "./clients/ClientAccountClaims";
import ClientAccountPolicies from "./clients/ClientAccountPolicies";
import ClientAccountPolicyView from "./clients/ClientAccountPolicyView";
import ClientAccountClaimsForm from "./clients/ClientAccountClaimsForm";
import ClientAccountClaimsView from "./clients/ClientAccountClaimsView";

import Reports from "./reports/Reports";
import ReportDetail from "./reports/ReportDetail";

function App() {
  const queryClient = new QueryClient();

  return (
    <ErrorBoundary>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <AppProvider>
            <Routes>
              <Route element={<AuthLayout />}>
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route
                  path="/client-account/:id"
                  element={<ClientAccountView />}
                />
              </Route>

              <Route element={<MainLayout />}>
                <Route element={<CheckLogin />}>
                  <Route path="" element={<Desktop />} />
                  <Route path="contact-us" element={<ContactUsWidget />} />
                  <Route path="desktop" element={<Desktop />} />
                  <Route path="approvals" element={<Approval />} />
                  {/* <Route path="" element={<Home />} /> */}
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/change-password" element={<Desktop />} />

                  <Route path="/clients">
                    <Route path="" element={<ClientList />} />
                    <Route path="add" element={<ClientForm />} />
                    <Route path=":id" element={<ClientView />}>
                      <Route
                        path=""
                        element={<PolicyList mini={true} scope="/clients/" />}
                      />
                      <Route
                        path="policies"
                        element={<PolicyList mini={true} scope="/clients/" />}
                      />
                      <Route path="account" element={<ClientAccount />} />
                      <Route
                        path="claims"
                        element={<ClaimList mini={true} />}
                      />
                      <Route
                        path="expiring"
                        element={<ClientExpiringPolices />}
                      />
                    </Route>
                    <Route
                      path=":id/edit"
                      element={<ClientForm scope="edit" />}
                    />
                  </Route>

                  {/* client user routes */}
                  <Route path="client-account">
                    <Route path="policies">
                      <Route path="" element={<ClientAccountPolicies />} />
                      <Route
                        path=":status"
                        element={<ClientAccountPolicies />}
                      />
                    </Route>
                    <Route
                      path="policy-item/:itemid"
                      element={<ClientAccountPolicyView />}
                    />
                    <Route path="claims">
                      <Route path="" element={<ClientAccountClaims />} />
                      <Route
                        path=":claimId"
                        element={<ClientAccountClaimsView />}
                      />
                      <Route
                        path="add/:clientPolicyId"
                        element={<ClientAccountClaimsForm />}
                      />
                    </Route>
                  </Route>

                  <Route path="/client-policies" element={<ClientPolicies />} />

                  <Route path="/companies">
                    <Route path="" element={<CompanyList />} />
                    <Route path="add" element={<CompanyForm />} />
                    <Route path="types">
                      <Route path="" element={<CompanyTypeList />} />
                      <Route path=":id" element={<CompanyTypeView />} />
                    </Route>
                    <Route path=":id" element={<CompanyView />}>
                      <Route path="" element={<BranchList />} />
                      <Route
                        path="products"
                        element={<ProductList scope="/companies/" />}
                      />
                      <Route
                        path="policies"
                        element={<PolicyList mini={true} scope="/companies/" />}
                      />
                      <Route
                        path="claims"
                        element={<BranchList scope="/companies/" />}
                      />
                      <Route
                        path="clients"
                        element={<ClientList scope="/companies/" />}
                      />
                      <Route path="branches/add" element={<BranchForm />} />
                      <Route
                        path="/companies/:id/edit"
                        element={<CompanyForm scope="edit" />}
                      />
                    </Route>
                  </Route>

                  <Route path="/branches/:id" element={<BranchView />}>
                    <Route
                      path=""
                      element={<PolicyList mini={true} scope="/branch/" />}
                    />
                    <Route
                      path="products"
                      element={<ProductList scope="/company/" />}
                    />
                    <Route
                      path="claims"
                      element={<BranchList scope="/branch/" />}
                    />
                    <Route
                      path="clients"
                      element={<ClientList scope="/branch/" />}
                    />
                    <Route path="edit" element={<BranchForm scope="edit" />} />
                  </Route>

                  <Route path="/policy-requirements">
                    <Route path="" element={<RequirementList />} />
                    <Route path="add" element={<RequirementForm />} />
                    <Route path=":id">
                      <Route path="" element={<RequirementForm />} />
                      <Route
                        path="edit"
                        element={<RequirementForm scope="edit" />}
                      />
                    </Route>
                  </Route>

                  <Route path="/policy-types">
                    <Route path="" element={<PolicyTypeList />} />
                    <Route path="add" element={<PolicyTypeForm />} />
                    <Route path=":id" element={<PolicyTypeView />}>
                      <Route path="" element={<PolicyTypeProductList />} />
                      <Route
                        path="/policy-types/:id/edit"
                        element={<PolicyTypeForm scope="edit" />}
                      />
                    </Route>
                  </Route>

                  <Route path="/products">
                    <Route path="" element={<ProductList />} />
                    <Route path="add" element={<ProductForm />} />
                    <Route path=":id">
                      <Route path="" element={<ProductView />} />
                      <Route
                        path="edit"
                        element={<ProductForm scope="edit" />}
                      />
                    </Route>
                  </Route>

                  <Route path="/policies">
                    <Route path="" element={<PolicyList />} />
                    <Route path="add">
                      <Route path="" element={<PolicyForm />} />
                      <Route path=":target">
                        <Route path="" element={<PolicyForm />} />
                        <Route path=":target_id" element={<PolicyForm />} />
                      </Route>
                    </Route>
                    <Route path=":id">
                      <Route path="" element={<PolicyView />} />
                      <Route
                        path="edit"
                        element={<PolicyForm scope="edit" />}
                      />
                      <Route path="item/:itemid">
                        <Route path="" element={<PolicyView scope="item" />} />
                        <Route
                          path=":tab"
                          element={<PolicyView scope="item" />}
                        />
                      </Route>
                    </Route>
                  </Route>

                  <Route path="/claims">
                    <Route path="" element={<ClaimList />} />
                    <Route path=":claimId" element={<ClaimView />} />
                    <Route path="add">
                      <Route path="" element={<ClaimForm />} />
                      <Route path=":clientPolicyId" element={<ClaimForm />} />
                    </Route>
                    <Route path="edit">
                      <Route
                        path=":clientPolicyId"
                        element={<ClaimForm scope="edit" />}
                      />
                    </Route>
                  </Route>

                  <Route path="/users" element={<UserList />} />
                  <Route path="/users/add" element={<UserForm />} />
                  <Route path="/users/:id" element={<UserView />} />
                  <Route path="edit" element={<UserForm scope="edit" />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/notifications">
                    <Route path="" element={<Notification />} />
                    <Route path="settings" element={<NotificationSetting />} />
                    <Route path="sms-messaging" element={<SmsMessaging />} />
                    <Route
                      path="email-messaging"
                      element={<EmailMessaging />}
                    />
                  </Route>
                  <Route path="/commissions">
                    <Route path="" element={<CommissionList />} />
                    <Route
                      path="deferred"
                      element={<DeferredCommissionList />}
                    />
                    <Route
                      path="deferred/:state"
                      element={<DeferredCommissionList />}
                    />
                  </Route>
                  <Route path="/policies/revoke-requests">
                    <Route path="" element={<RevokeRequestList />} />
                    <Route path=":status" element={<RevokeRequestList />} />
                  </Route>
                  <Route path="/reports">
                    <Route path="" element={<Reports />} />
                    <Route path=":report" element={<ReportDetail />} />
                  </Route>
                </Route>
              </Route>

              <Route path="/" element={<AuthLayout />}>
                <Route path="*" element={<Missing />} />
              </Route>
            </Routes>
          </AppProvider>
        </QueryClientProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
